// Pure reader for the raw Enterprise template row the Standard send panel (GIF/LOCATION/LTO/
// voice-call) needs to shape itself around. `details` is mapTemplate()'s (helper/utils.js) output
// — a straight `{...template}` spread of the raw list-row, no DTO in between. Confirmed against
// Templates/Form/prefill.js, which reads the IDENTICAL row shape off the SAME list endpoint
// (getTemplates()) and already relies on details.buttons (prefill.js:360/675,
// `Array.isArray(source.buttons)`) and details.limitedTimeOffer (prefill.js:384/460-462/508) —
// both real, not a guess; `buttons` is NOT absent on Message-tab rows.
import { countBraceTokens } from './validators';

// details.type doubles as the header's media kind (see Templates/Form/prefill.js's own
// mediaHeaderTypes comment) — TEXT/undefined means no media header (text or none instead).
export const MEDIA_HEADER_KINDS = ['IMAGE', 'VIDEO', 'DOCUMENT', 'GIF', 'LOCATION'];

// Subset of MEDIA_HEADER_KINDS that takes a media URL at send time. LOCATION is a media header
// for classification (headerKind) but sends lat/lng/name/address, not a URL — callers that render
// or validate a media-URL field must use this, not MEDIA_HEADER_KINDS.
export const SEND_MEDIA_KINDS = ['IMAGE', 'VIDEO', 'DOCUMENT', 'GIF'];

// headerKind -> the lowercase wire messageType a send of that header carries. 'none'/'text' both send as
// 'text': the body is what carries the message either way. Mirrors src/webhook/messageType.js, keyed by our
// headerKind vocabulary instead of the raw details.type (which this folder may not import).
export const MESSAGE_TYPE_BY_HEADER_KIND = {
  none: 'text',
  text: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  GIF: 'gif',
  LOCATION: 'location',
};

// button_type -> the draft field its dynamic value lives in, and what the carry-over notice calls it. Any other
// button_type (STATIC, NONE, COMPLEX, ...) keeps no value in either field.
export const DYNAMIC_BUTTON_FIELDS = {
  DYNAMIC: { field: 'buttonUrlParam', label: 'button link' },
  DYNAMIC_LINK_TRACK: { field: 'ctaButtonUrl', label: 'tracked link' },
};

/**
 * The one rule for which buttons take a typed value at send time.
 * @param {{ type?: string, urlType?: string }} [button] - a TemplateButtonInfo
 * @returns {'code'|'suffix'|'link'|null} 'code' for COPY_CODE, 'suffix' for a DYNAMIC URL, 'link' for a
 *   DYNAMIC_LINK_TRACK URL; null for the rest (static URL, phone, quick reply, voice call)
 */
export function buttonInputKind(button) {
  if (button?.type === 'COPY_CODE') return 'code';
  if (button?.type === 'URL' && button.urlType === 'DYNAMIC') return 'suffix';
  if (button?.type === 'URL' && button.urlType === 'DYNAMIC_LINK_TRACK') return 'link';
  return null;
}

/**
 * Enterprise's rule for a template send, ChannelHSMTuple.isTypeTemplate(): the listing's button_type is anything but NONE or
 * empty (gupshup-common SMSCommon/src/com/webaroo/enterprise/ChannelHSMTuple.java:309-312). A header or a footer alone is a
 * template on the console but not a template send here: sending it as one fails Enterprise's TemplateTypeFilter
 * (MSG_DOES_NOT_MATCH_HSM_TEMPLATE for a text send, INVALID_TEMPLATE_ID for a media send).
 * @param {object} [details] - a mapTemplate() row's `.details`
 * @returns {boolean}
 */
export const isTypeTemplate = (details) => !!details?.button_type && details.button_type !== 'NONE';

// The same count as @gs/gs-integration-component's variableCount (Helper/Utils.tsx:188-191), the count behind its
// variable slots, without its backtracking: the library's pattern takes exponential time on an unclosed {{ followed
// by ~25 characters (24 take ~8 s); the library itself still has the slow one. Not countBraceTokens: that one wants
// digits, this one counts any {{...}}. Delete when this moves into the library.
/** Number of {{...}} variable slots in `text`, counted the way the shared component counts them. */
export const libraryVariableCount = (text) => (String(text || '').match(/\{\{(?:[^}]\}?)*\}\}/g) || []).length;

/**
 * @typedef {object} TemplateButtonInfo
 * @property {number} index - 0-based position in details.buttons, and the wire `index` every
 *   button component below is keyed by
 * @property {string} [type] - raw wire BUTTON_TYPE value, e.g. 'URL', 'COPY_CODE', 'VOICE_CALL',
 *   'QUICK_REPLY', 'PHONE_NUMBER'
 * @property {string} [urlType] - URL buttons only: 'STATIC'|'DYNAMIC'|'DYNAMIC_LINK_TRACK'
 * @property {string} [text] - button label
 * @property {string} [url] - URL buttons only
 */

/**
 * @typedef {object} TemplateDescription
 * @property {'none'|'text'|'IMAGE'|'VIDEO'|'DOCUMENT'|'GIF'|'LOCATION'} headerKind
 * @property {number} headerVarCount - {{n}} tokens in details.header; 0 for a media/none header
 * @property {number} bodyVarCount - {{n}} tokens in details.body
 * @property {TemplateButtonInfo[]} buttons
 * @property {boolean} isLto - details.button_type === 'LTO'
 * @property {boolean} ltoHasExpiration - details.limitedTimeOffer?.has_expiration
 * @property {boolean} hasVoiceCall - any button is type VOICE_CALL
 * @property {string} buttonType - raw details.button_type ('NONE' when absent), e.g. 'NONE'|
 *   'STATIC'|'DYNAMIC'|'DYNAMIC_LINK_TRACK'|'COMPLEX'|'LTO'|'CAROUSEL'
 */

/**
 * @param {object} [details] - a mapTemplate() row's `.details`
 * @returns {TemplateDescription}
 */
export function describeTemplate(details) {
  const d = details || {};
  const buttonType = d.button_type || 'NONE';

  let headerKind = 'none';
  if (d.header) {
    headerKind = 'text';
  } else if (MEDIA_HEADER_KINDS.includes(d.type)) {
    headerKind = d.type;
  }

  const rawButtons = Array.isArray(d.buttons) ? d.buttons : [];
  const buttons = rawButtons.map((button, index) => ({
    index,
    type: button?.type,
    urlType: button?.urlType,
    text: button?.text,
    url: button?.url,
  }));

  return {
    headerKind,
    headerVarCount: headerKind === 'text' ? countBraceTokens(d.header) : 0,
    bodyVarCount: countBraceTokens(d.body ?? d.data),
    buttons,
    isLto: buttonType === 'LTO',
    ltoHasExpiration: Boolean(d.limitedTimeOffer?.has_expiration),
    hasVoiceCall: buttons.some((button) => button.type === 'VOICE_CALL'),
    buttonType,
  };
}

/**
 * True when the template sends its buttons as waTemplateJson, never in buttonUrlParam / ctaButtonUrl: COMPLEX,
 * LTO and carousel templates, and any voice-call template (the Standard panel builds those whole).
 * @param {TemplateDescription} described - describeTemplate()'s output
 * @returns {boolean}
 */
export const buttonsTravelAsJson = (described) => described.buttonType === 'COMPLEX'
  || described.buttonType === 'CAROUSEL'
  || described.isLto
  || described.hasVoiceCall;
